import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, User } from "lucide-react";
import { fetchBlogPost } from "@/lib/api";

function renderContent(md) {
  return md.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return <h2 key={i} className="mt-10 font-display text-2xl font-semibold text-white">{block.replace("## ", "")}</h2>;
    }
    if (block.startsWith("- ")) {
      const items = block.split("\n").map((l) => l.replace(/^- /, ""));
      return (
        <ul key={i} className="mt-4 space-y-2">
          {items.map((it, j) => (
            <li key={j} className="flex items-start gap-2 text-white/65"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" />{it}</li>
          ))}
        </ul>
      );
    }
    return <p key={i} className="mt-5 text-base leading-relaxed text-white/65">{block}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center bg-ink-0 px-5 text-center">
        <p className="font-display text-3xl text-white">Article not found</p>
        <Link to="/insights" className="mt-6 text-brand-cyan">← Back to Insights</Link>
      </main>
    );
  }
  if (!post) {
    return <main className="flex min-h-screen items-center justify-center bg-ink-0"><p className="font-mono text-sm text-white/40">Loading...</p></main>;
  }

  return (
    <main className="relative min-h-screen bg-ink-0 pt-28" data-testid="blog-post">
      <div className="relative h-[42vh] min-h-[320px] w-full overflow-hidden">
        <img src={post.cover} alt={post.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-0 via-ink-0/60 to-ink-0/30" />
      </div>
      <article className="relative mx-auto -mt-28 max-w-3xl px-5 pb-28 lg:px-8">
        <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-brand-cyan" data-testid="back-to-insights">
          <ArrowLeft className="h-4 w-4" /> Back to Insights
        </Link>
        <span className="mt-6 inline-block rounded-full border border-brand-cyan/30 bg-brand-cyan/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-cyan">{post.category}</span>
        <h1 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">{post.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-4 border-b border-white/10 pb-6 font-mono text-xs text-white/45">
          <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{post.author}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{post.read_time}</span>
          <span>{post.published_at}</span>
        </div>
        <div className="mt-2">{renderContent(post.content)}</div>
      </article>
    </main>
  );
}
